import { UseFormReset } from 'react-hook-form';

import { useAuthUser } from 'auth/hooks';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { useApplySelectedSpaToLines } from 'quotes/components/QuotePartSelectSpa';
import { useQuotePartActions, useRefreshQuotePartRows } from 'quotes/hooks';
import {
  mapBulkQuotePartsCalculatePricing,
  mapSingleQuotePartCalculatePricing,
} from 'quotes/mappers';
import { useCustomerQuotePartsContext, useSelectedQuotePartContext } from 'quotes/states';
import { ICalculatePriceForm, ICustomerQuote, ICustomerQuotePart } from 'quotes/types';
import { getSelectedSpaPick } from 'quotes/utils';

import QuotePartUpdateMarginFormView from './QuotePartUpdateMarginFormView';
import { quotePartUpdateMargin, quotePartUpdateMarginBulk } from './schema';
import { getDefaultValues } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess: () => void;
  quote?: ICustomerQuote;
  isAlternateVersionEnabled?: boolean;
  interactive?: boolean;
}

const QuotePartUpdateMarginForm = ({
  isOpen = false,
  onClose,
  onSubmitSuccess,
  quote,
  isAlternateVersionEnabled = false,
  interactive = false,
}: IProps) => {
  const { selectedRows, updateRows } = useAgGridClientTableContext();
  const { currentQuoteVersion } = useCustomerQuotePartsContext();
  const refreshQuotePartRows = useRefreshQuotePartRows(quote?.id, currentQuoteVersion);
  const { selectedQuotePart: inlineDrawerSelectedQuotePart } = useSelectedQuotePartContext();

  const selectedQuotePart =
    inlineDrawerSelectedQuotePart ?? (selectedRows.length === 1 ? selectedRows[0] : undefined);

  const selectedQuoteParts = selectedQuotePart ? [selectedQuotePart] : selectedRows;

  const { user } = useAuthUser();
  const { clearDrawerNotification, clearFormNotification } = useNotificationContext();
  const { upsertQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const { promptApplyToOtherLines, applyToLinesConfirmation } = useApplySelectedSpaToLines({
    quoteId: quote?.id,
    onApplied: onSubmitSuccess,
  });

  const onSubmit =
    (reset: UseFormReset<ICalculatePriceForm>) => async (data: ICalculatePriceForm) => {
      handleOnClose(reset, true);

      const payload = data.id
        ? mapSingleQuotePartCalculatePricing(selectedQuoteParts, data, isAlternateVersionEnabled)
        : mapBulkQuotePartsCalculatePricing(selectedQuoteParts, data, isAlternateVersionEnabled);

      const isSavedQuotePart = ({ id }: ICustomerQuotePart) => !!data.id && id === data.id;

      // Captured before the save: the saved row comes back carrying the new pick.
      const previousSelectedSpa = getSelectedSpaPick(selectedQuotePart as ICustomerQuotePart);
      const { affectedQuotePartIds, success, quoteParts } = await upsertQuoteParts(payload, {
        onSuccess: onSubmitSuccess,
      });

      if (!success) {
        return;
      }

      updateRows(
        { update: quoteParts },
        {
          refreshCells: true,
          redrawRows: true,
        }
      );

      refreshQuotePartRows(affectedQuotePartIds);

      promptApplyToOtherLines({
        savedQuotePart: quoteParts?.find(isSavedQuotePart),
        previousSelectedSpa,
      });
    };

  // Submit closes here too, bypassing the Form's clear. Scoped: TOP carries the job toasts.
  const handleOnClose = (reset: UseFormReset<ICalculatePriceForm>, closeSelection = false) => {
    clearFormNotification();
    clearDrawerNotification();
    onClose(closeSelection);
    reset();
  };

  const isSingleSelection = selectedQuoteParts.length === 1;
  const schema = isSingleSelection
    ? quotePartUpdateMargin(
        isAlternateVersionEnabled,
        Boolean(selectedQuoteParts[0]?.supplier?.isShipAndDebit),
        selectedQuoteParts[0]
      )
    : quotePartUpdateMarginBulk(isAlternateVersionEnabled);

  return (
    <>
      <FormProvider
        key={JSON.stringify(selectedQuoteParts)}
        schema={schema}
        defaultValues={getDefaultValues(selectedQuoteParts, user?.margins)}
      >
        <QuotePartUpdateMarginFormView
          isOpen={isOpen}
          onClose={handleOnClose}
          onSubmit={onSubmit}
          selectedQuoteParts={selectedQuoteParts}
          margins={user?.margins}
          quote={quote}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          interactive={interactive}
        />
      </FormProvider>

      {/* Outside the form: onSubmit closes the drawer before the save even resolves. */}
      {applyToLinesConfirmation}
    </>
  );
};

export default QuotePartUpdateMarginForm;
